import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, X, Trash2, ArrowRight } from "lucide-react";
import { useCart } from "../../contexts/CartContext";
import { useI18n } from "../../i18n";

const StickyCart = () => {
  const { lines, count, subtotal, setQty, remove } = useCart();
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [lastCount, setLastCount] = useState(count);

  useEffect(() => {
    if (count > lastCount) {
      setPulse(true);
      setOpen(true);
      const t1 = setTimeout(() => setPulse(false), 600);
      const t2 = setTimeout(() => setOpen(false), 3500);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    setLastCount(count);
  }, [count]); // eslint-disable-line

  if (count === 0) return null;

  return (
    <>
      {/* Floating Cart Button */}
      <button
        data-testid="sticky-cart-toggle"
        onClick={() => setOpen(!open)}
        className={`fixed z-40 bottom-5 right-5 md:bottom-8 md:right-8 bg-ink text-white w-14 h-14 md:w-16 md:h-16 flex items-center justify-center shadow-[0_18px_40px_-12px_rgba(0,0,0,0.5)] hover:bg-terracotta transition-colors ${pulse ? "animate-bounce" : ""}`}
        aria-label="Cart"
      >
        <ShoppingBag className="w-5 h-5 md:w-6 md:h-6" />
        <span className="absolute -top-1.5 -right-1.5 bg-terracotta text-white text-[10px] font-semibold rounded-full w-5 h-5 flex items-center justify-center border-2 border-white">
          {count}
        </span>
      </button>

      {/* Mini cart drawer */}
      <div
        className={`fixed z-50 bottom-24 right-5 md:right-8 w-[90vw] sm:w-96 bg-white border border-black/10 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.35)] transition-all duration-300 ${open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"}`}
        data-testid="sticky-cart-drawer"
      >
        <div className="flex items-center justify-between p-4 border-b border-black/5">
          <div>
            <div className="font-label text-ink-muted">{t("cart")}</div>
            <div className="font-display text-lg">{count} {count === 1 ? (lang === "nl" ? "artikel" : "item") : (lang === "nl" ? "artikelen" : "items")}</div>
          </div>
          <button onClick={() => setOpen(false)} className="text-ink-muted hover:text-ink"><X className="w-5 h-5" /></button>
        </div>
        <div className="max-h-72 overflow-y-auto divide-y divide-black/5">
          {lines.filter((l) => l && l.snapshot).map((l) => (
            <div key={l.product_id} className="p-3 flex gap-3">
              <Link to={`/product/${l.snapshot?.slug || ""}`} onClick={() => setOpen(false)} className="w-14 h-16 bg-secondary-warm overflow-hidden shrink-0">
                <img src={l.snapshot?.image} alt="" className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{lang === "nl" ? l.snapshot?.name_nl : l.snapshot?.name_en}</div>
                <div className="flex items-center justify-between mt-1.5">
                  <div className="flex items-center border border-black/15 text-xs">
                    <button onClick={() => setQty(l.product_id, l.qty - 1)} className="w-6 h-6">−</button>
                    <span className="w-6 text-center">{l.qty}</span>
                    <button onClick={() => setQty(l.product_id, l.qty + 1)} className="w-6 h-6">+</button>
                  </div>
                  <button onClick={() => remove(l.product_id)} className="text-ink-muted hover:text-terracotta"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="text-sm font-display">€{(l.qty * l.snapshot.price).toFixed(2)}</div>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-black/5">
          <div className="flex justify-between mb-3">
            <span className="font-label">{t("subtotal")}</span>
            <span className="font-display text-lg" data-testid="sticky-cart-subtotal">€{subtotal.toFixed(2)}</span>
          </div>
          <div className="flex gap-2">
            <Link to="/cart" onClick={() => setOpen(false)} className="btn-secondary text-xs flex-1 py-2.5">{t("cart")}</Link>
            <Link to="/checkout" onClick={() => setOpen(false)} className="btn-primary text-xs flex-1 py-2.5">{t("checkout")} <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default StickyCart;
